import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { collection, getDocs, doc, getDoc, setDoc } from "firebase/firestore";
import { db, auth } from "../utils/firebase";
import { Spinner } from "../components";
import { Navigate, useNavigate } from "react-router-dom";

// Define default roles and permissions
const defaultPermissions = {
  Owner: {
    canAddNote: true,
    recordAlumniEvents: true,
    viewSponsorship: true,
    seeApplications: true,
    canViewNotes: true,
    viewSettings: true,
    canApproveDenyOvernight: true,
    updatePermissions: true,
    viewPhases: true,
    updateUsers: true,
    updateHouseSettings: true,
    updateUserRoles: true,
    seeBanList: true,
    deleteResidentDoc: true,
    updateResidentDoc: true,
    manageBanList: true,
    addRecoveryCapital: true,
    manageUACounts: true,
    viewWaitlist: true,
    viewAnalytics: true,
    saveSnapshots: true,
    canViewAnalyticData: true,
    viewRecoveryCapital: true,
    deleteToDo: true,
    exportRecoveryData: true,
    viewResidents: true,
    dischargeResidents: true,
    reinstateResidents: true,
    viewProfile: true,
    addForms: true,
    createForms: true,
    viewPaymentHistory: true,
    updateResidentInfo: true,
    exportResidentData: true,
    viewAlumni: true,
    viewAlumniProfile: true,
    viewBeds: true,
    updateBeds: true,
    viewUa: true,
    addUa: true,
    viewUaHistory: true,
    canAddResidents: true,
    viewPayments: true,
    addPayment: true,
    updatePayments: true,
    viewInvoice: true,
    updateInvoices: true,
    accessOwnerDashboard: true,
    viewDashboard: true,
    viewResidentDashboard: true,
    updateSchedule: true,
  },
  Admin: {
    viewSettings: true,
    canApproveDenyOvernight: true,
    recordAlumniEvents: true,
    canViewNotes: true,
    viewSponsorship: true,
    seeApplications: true,
    viewPhases: true,
    canAddNote: true,
    updatePermissions: false,
    updateUsers: false,
    updateHouseSettings: false,
    updateUserRoles: false,
    seeBanList: true,
    deleteResidentDoc: false,
    updateResidentDoc: true,
    manageBanList: true,
    manageUACounts: true,
    addRecoveryCapital: true,
    viewAnalytics: true,
    reinstateResidents: true,
    saveSnapshots: true,
    viewWaitlist: true,
    viewRecoveryCapital: true,
    canViewAnalyticData: true,
    deleteToDo: false,
    canAddResidents: true,
    exportRecoveryData: false,
    viewResidents: true,
    dischargeResidents: true,
    viewProfile: true,
    addForms: true,
    createForms: true,
    viewPaymentHistory: true,
    updateSchedule: true,
    updateResidentInfo: true,
    exportResidentData: false,
    viewAlumni: true,
    viewAlumniProfile: true,
    viewBeds: true,
    updateBeds: true,
    viewUa: true,
    addUa: true,
    viewUaHistory: true,
    viewPayments: true,
    addPayment: true,
    updatePayments: false,
    viewInvoice: true,
    updateInvoices: true,
    viewDashboard: true,
    accessOwnerDashboard: false,
    viewResidentDashboard: true,
  },
  Staff: {
    viewSettings: false,
    canApproveDenyOvernight: true,
    canViewNotes: true,
    viewSponsorship: true,
    viewPhases: true,
    canAddNote: true,
    updatePermissions: false,
    recordAlumniEvents: false,
    seeApplications: true,
    updateUsers: false,
    updateHouseSettings: false,
    updateUserRoles: false,
    seeBanList: false,
    deleteResidentDoc: false,
    updateResidentDoc: false,
    manageBanList: false,
    manageUACounts: false,
    addRecoveryCapital: true,
    viewAnalytics: true,
    saveSnapshots: false,
    reinstateResidents: false,
    viewRecoveryCapital: true,
    deleteToDo: true,
    exportRecoveryData: false,
    canViewAnalyticData: true,
    viewResidents: true,
    dischargeResidents: false,
    viewWaitlist: true,
    viewProfile: true,
    addForms: true,
    createForms: false,
    viewPaymentHistory: false,
    updateResidentInfo: false,
    exportResidentData: false,
    updateSchedule: true,
    canAddResidents: true,
    viewAlumni: true,
    viewAlumniProfile: true,
    viewBeds: true,
    updateBeds: false,
    viewUa: true,
    addUa: true,
    viewUaHistory: true,
    viewPayments: false,
    addPayment: false,
    updatePayments: false,
    viewInvoice: false,
    updateInvoices: false,
    viewDashboard: true,
    accessOwnerDashboard: false,
    viewResidentDashboard: true,
  },
  Leader: {
    viewSettings: false,
    canViewNotes: true,
    canAddNote: true,
    canApproveDenyOvernight: false,
    updatePermissions: false,
    seeApplications: false,
    updateUsers: false,
    updateHouseSettings: false,
    viewSponsorship: true,
    updateUserRoles: false,
    viewPhases: false,
    seeBanList: false,
    deleteResidentDoc: false,
    updateResidentDoc: false,
    manageBanList: false,
    recordAlumniEvents: false,
    manageUACounts: false,
    viewAnalytics: true,
    canViewAnalyticData: false,
    reinstateResidents: false,
    addRecoveryCapital: false,
    saveSnapshots: false,
    viewRecoveryCapital: true,
    deleteToDo: false,
    viewWaitlist: false,
    exportRecoveryData: false,
    viewResidents: true,
    dischargeResidents: false,
    viewProfile: false,
    addForms: false,
    createForms: false,
    canAddResidents: false,
    viewPaymentHistory: false,
    updateResidentInfo: false,
    exportResidentData: false,
    viewAlumni: false,
    updateSchedule: true,
    viewAlumniProfile: false,
    viewBeds: true,
    updateBeds: false,
    viewUa: true,
    addUa: true,
    viewUaHistory: false,
    viewPayments: false,
    addPayment: false,
    updatePayments: false,
    viewInvoice: false,
    updateInvoices: false,
    viewDashboard: true,
    accessOwnerDashboard: false,
    viewResidentDashboard: false,
  },
  Resident: {
    seeBanList: false,
    canViewNotes: false,
    canAddNote: false,
    viewPhases: false,
    deleteResidentDoc: false,
    canApproveDenyOvernight: false,
    updateResidentDoc: false,
    manageBanList: false,
    viewSponsorship: false,
    seeApplications: false,
    manageUACounts: false,
    viewAnalytics: false,
    canViewAnalyticData: false,
    reinstateResidents: false,
    addRecoveryCapital: false,
    saveSnapshots: false,
    updateSchedule: false,
    recordAlumniEvents: false,
    viewRecoveryCapital: false,
    deleteToDo: false,
    viewWaitlist: false,
    exportRecoveryData: false,
    viewResidents: false,
    dischargeResidents: false,
    viewProfile: false,
    addForms: false,
    createForms: false,
    canAddResidents: false,
    viewPaymentHistory: false,
    updateResidentInfo: false,
    exportResidentData: false,
    viewAlumni: false,
    viewAlumniProfile: false,
    viewBeds: false,
    updateBeds: false,
    viewUa: false,
    addUa: false,
    viewUaHistory: false,
    viewPayments: false,
    addPayment: false,
    updatePayments: false,
    viewInvoice: false,
    updateInvoices: false,
    viewDashboard: false,
    accessOwnerDashboard: false,
    viewResidentDashboard: true,
  },
};

const fetchOrganizationData = async (orgId) => {
  try {
    const houseSettingsRef = doc(
      db,
      `organizations/${orgId}/houseSettings/initial`
    );
    const houseSettingsSnap = await getDoc(houseSettingsRef);
    const houseSettings = houseSettingsSnap.exists()
      ? houseSettingsSnap.data()
      : null;

    const defaultFeatures = {
      // Staff Dashboard Features
      staff: {
        recoveryCapital: true,
        payments: true,
        records: true,
        uaRecording: true,
        uaScheduling: true,
        analytics: true,
        phases: true,
        sponsorship: true,
        customForms: true,
        applications: true,
        bedPlatform: true,
        ownerDashboard: true,
        schedule: true,
      },
      // Resident Dashboard Features
      resident: {
        overnightRequest: true,
        payments: true,
        phases: true,
        recoveryCapital: true,
        ua: true,
        geoLocation: true,
        schedule: true,
      },
    };

    // Get features from houseSettings or use defaults
    let features = houseSettings?.features || defaultFeatures;

    if (!features.staff && !features.resident) {
      features = {
        staff: {
          recoveryCapital: features.recoveryCapital || true,
          payments: features.payments || true,
          records: features.records || true,
          uaRecording: features.uaRecording || true,
          uaScheduling: features.uaScheduling || true,
          analytics: features.analytics || true,
          phases: features.phases || true,
          sponsorship: features.sponsorship || true,
          customForms: features.customForms || true,
          applications: features.applications || true,
          bedPlatform: features.bedPlatform || true,
          ownerDashboard: features.ownerDashboard || true,
          schedule: true, // Default to true for new feature
        },
        resident: {
          overnightRequest: features.overnightRequest || true,
          payments: features.payments || true, // Inherit from staff setting
          phases: features.phases || true, // Inherit from staff setting
          recoveryCapital: features.recoveryCapital || true, // Inherit from staff setting
          ua: (features.uaRecording || true) && (features.uaScheduling || true), // Only if both are enabled
          geoLocation: true, // New feature
          schedule: true, // New feature
        },
      };
    }

    // If the document exists but features don't, update it silently in the background
    if (houseSettingsSnap.exists() && !houseSettings.features) {
      try {
        console.log(`Adding default features to organization ${orgId}`);
        await updateDoc(houseSettingsRef, {
          features: defaultFeatures,
          featuresLastUpdated: new Date(),
        });
        // Use the defaults we just saved
        features = defaultFeatures;
      } catch (error) {
        console.error(`Failed to add default features to org ${orgId}:`, error);
        // Continue with defaults anyway
      }
    }

    const residentsSnapshot = await getDocs(
      collection(db, `organizations/${orgId}/residents`)
    );
    const residents = residentsSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    const formattedHouses = {};

    if (houseSettings?.houses && Array.isArray(houseSettings.houses)) {
      houseSettings.houses.forEach((house) => {
        let totalBeds = 0;
        house.floors?.forEach((floor) => {
          floor.rooms?.forEach((room) => {
            totalBeds += room.beds?.length || 0;
          });
        });

        formattedHouses[house.name] = {
          totalBeds,
          occupied: 0,
          color: house.color,
          fee: house.fee,
          floors: house.floors,
          name: house.name,
        };
      });
    }

    // Calculate current total beds
    let currentTotalBeds = 0;
    if (houseSettings?.houses) {
      currentTotalBeds = houseSettings.houses.reduce((total, house) => {
        return (
          total +
          house.floors.reduce((floorTotal, floor) => {
            return (
              floorTotal +
              floor.rooms.reduce((roomTotal, room) => {
                return roomTotal + room.beds.length;
              }, 0)
            );
          }, 0)
        );
      }, 0);
    }

    residents.forEach((resident) => {
      const houseName = resident.house;
      if (houseName && formattedHouses[houseName]) {
        if (resident.currentResident === true) {
          formattedHouses[houseName].occupied += 1;
        }
      }
    });

    const permissions = houseSettings?.permissions || {};

    return {
      organizationName: houseSettings?.organizationName || "",
      housesData: formattedHouses,
      residents,
      permissions,
      features,
      houseSettings,
      needsBedUpdate: currentTotalBeds > houseSettings?.paidBedCount,
      paidBedCount: houseSettings?.paidBedCount || 0,
      currentTotalBeds,
    };
  } catch (error) {
    console.error("[ERROR] Fetching organization data:", error);
    throw error;
  }
};

const AuthenticationContext = createContext(null);

const AuthenticationProvider = ({ children }) => {
  const [user, loading] = useAuthState(auth);
  const navigate = useNavigate();
  const [authState, setAuthState] = useState({
    firebaseLoaded: false,
    organizationLoaded: false,
    isAuthenticated: false,
    organizationId: null,
    organizationData: null,
    role: null,
    permissions: null,
    pendingStatus: null,
    loading: true,
    error: null,
    features: {},
  });

  useEffect(() => {
    if (!loading) {
      setAuthState((prev) => ({
        ...prev,
        firebaseLoaded: true,
        isAuthenticated: !!user,
        loading: false,
        user,
      }));

      // Check if current path is a public route with token
      const currentPath = window.location.pathname;
      const urlParams = new URLSearchParams(window.location.search);
      const hasPublicToken = urlParams.has("token");

      // Allow public access to recovery capital analytics with token
      const isPublicRecoveryCapitalRoute =
        currentPath === "/analytics-public" && hasPublicToken;

      // Add this: Immediately navigate to login if auth fails, BUT exclude public routes
      if (
        !user &&
        !isPublicRecoveryCapitalRoute &&
        currentPath !== "/login" &&
        currentPath !== "/welcome" &&
        currentPath !== "/signup" &&
        !currentPath.startsWith("/public-forms")
      ) {
        navigate("/login", { replace: true });
      }
    }
  }, [user, loading, navigate]);

  // Fetch organization data once Firebase auth is loaded
  useEffect(() => {
    if (!authState.firebaseLoaded || !user) return;

    // In AuthenticationProvider component
    const initOrgAuth = async () => {
      try {
        const orgsSnapshot = await getDocs(collection(db, "organizations"));
        const pendingUserRef = doc(db, "pendingUsers", user.email);
        const pendingUserDoc = await getDoc(pendingUserRef);

        // Check organizations first
        for (const org of orgsSnapshot.docs) {
          const orgId = org.id;
          const userDocRef = doc(
            db,
            "organizations",
            orgId,
            "users",
            user.email
          );
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            const orgData = await fetchOrganizationData(orgId);

            // Ensure default permissions for Owner and Resident are always set
            const updatedPermissions = {
              ...orgData.permissions,
              Owner: { ...defaultPermissions.Owner }, // Default Owner permissions
              Resident: { ...defaultPermissions.Resident }, // Default Resident permissions
            };

            setAuthState((prev) => ({
              ...prev,
              organizationLoaded: true,
              isAuthenticated: true,
              organizationId: orgId,
              organizationData: {
                ...orgData,
                permissions: updatedPermissions, // Updated permissions with defaults
              },
              role: userData.role,
              permissions: updatedPermissions[userData.role] || {}, // Permissions for the current user role
              pendingStatus: "Completed",
              loading: false,
              error: null,
            }));
            console.log("Auth state updated with role:", userData.role);
            return;
          }
        }

        // Check pending status
        if (pendingUserDoc.exists()) {
          const pendingUserData = pendingUserDoc.data();
          setAuthState((prev) => ({
            ...prev,
            organizationLoaded: true,
            isAuthenticated: true,
            pendingStatus: pendingUserData.status || "Approved",
            loading: false,
          }));
          return;
        }

        // Create new pending user if not found
        await setDoc(pendingUserRef, {
          email: user.email,
          status: "Approved",
          createdAt: new Date(),
        });

        setAuthState((prev) => ({
          ...prev,
          organizationLoaded: true,
          isAuthenticated: true,
          pendingStatus: "Approved",
          loading: false,
        }));
      } catch (error) {
        console.error("[ERROR] Loading organization data:", error);
        setAuthState((prev) => ({
          ...prev,
          organizationLoaded: true,
          error,
          loading: false,
        }));
      }
    };

    initOrgAuth();
  }, [authState.firebaseLoaded, user]);

  // Logout functionality
  const logout = async () => {
    try {
      // Set loading state first to prevent navigation race conditions
      setAuthState((prev) => ({
        ...prev,
        loading: true,
      }));

      // Sign out from Firebase
      await auth.signOut();

      // Reset auth state
      setAuthState({
        firebaseLoaded: true, // Keep this true to prevent re-initialization
        organizationLoaded: false,
        isAuthenticated: false,
        organizationId: null,
        organizationData: null,
        role: null,
        permissions: null,
        pendingStatus: null,
        loading: false,
        error: null,
      });

      // Force navigation to login page
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Error during logout:", error);
      // Reset error state and ensure we're not stuck in loading
      setAuthState((prev) => ({
        ...prev,
        loading: false,
        error: error,
      }));
    }
  };

  return (
    <AuthenticationContext.Provider
      value={{
        ...authState,
        logout,
        checkPermission: (permission) => {
          const hasPermission = authState.permissions?.[permission] || false;
          return hasPermission;
        },
        checkFeature: (feature) => {
          // If the feature isn't in the object, default to enabled
          return authState.organizationData?.features?.[feature] !== false;
        },
        defaultPermissions,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthenticationContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthenticationProvider");
  }

  return {
    ...context,
    firebaseLoaded: !!context.firebaseLoaded,
    organizationLoaded: !!context.organizationLoaded,
    isAuthenticated: !!context.isAuthenticated,
    isPending: context.pendingStatus === "pending",
    isApproved: context.pendingStatus === "approved",
    isLoading: context.loading,
    user: context.user,
    role: context.role,
    organizationId: context.organizationId,
    organizationData: context.organizationData,
    checkPermission: context.checkPermission,
    checkFeature: (feature, category = "staff") => {
      if (!context.organizationData?.features) return true;

      const features = context.organizationData.features;

      // Handle dependency logic for resident features
      if (category === "resident") {
        // Special dependencies for resident features
        if (feature === "payments") {
          return (
            features.resident?.payments !== false &&
            features.staff?.payments !== false
          );
        }
        if (feature === "phases") {
          return (
            features.resident?.phases !== false &&
            features.staff?.phases !== false
          );
        }
        if (feature === "recoveryCapital") {
          return (
            features.resident?.recoveryCapital !== false &&
            features.staff?.recoveryCapital !== false
          );
        }
        if (feature === "ua") {
          return (
            features.resident?.ua !== false &&
            features.staff?.uaRecording !== false &&
            features.staff?.uaScheduling !== false
          );
        }

        // Standard feature check for resident category
        return features.resident?.[feature] !== false;
      }

      // Standard feature check for staff category
      return features.staff?.[feature] !== false;
    },
  };
};

export const ProtectedRoute = ({
  children,
  requiredPermission,
  allowedRoles,
  allowedEmails,
  fallbackPath = "/login",
  additionalCheck,
}) => {
  const {
    isAuthenticated,
    isLoading,
    role,
    checkPermission,
    user,
    firebaseLoaded,
    organizationLoaded,
    organizationId,
    organizationData,
  } = useAuth();
  const navigate = useNavigate();

  const [additionalCheckPassed, setAdditionalCheckPassed] = useState(true);

  useEffect(() => {
    // Special case for Leaders viewing residentdashboard
    if (
      requiredPermission === "viewResidentDashboard" &&
      role === "Leader" &&
      !checkPermission("viewResidentDashboard")
    ) {
      // Get the resident ID from sessionStorage
      const viewingResidentId = sessionStorage.getItem("viewingResidentId");

      if (viewingResidentId) {
        // Allow access if Leader has the session storage set (meaning they clicked "My Resident Dashboard")
        return; // Skip other checks since we're allowing this Leader access
      } else {
        // No session storage, redirect to main dashboard
        navigate("/");
        return;
      }
    }

    // Regular additional checks
    if (additionalCheck) {
      additionalCheck(organizationId, organizationData)
        .then((result) => setAdditionalCheckPassed(result))
        .catch((err) => {
          console.error("Additional check failed:", err);
          setAdditionalCheckPassed(false);
        });
    }
  }, [
    additionalCheck,
    organizationId,
    organizationData,
    requiredPermission,
    role,
    checkPermission,
    user,
    navigate,
  ]);

  if (isLoading || !firebaseLoaded || !organizationLoaded) {
    return <Spinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to={fallbackPath} replace />;
  }

  if (allowedEmails?.length && !allowedEmails.includes(user?.email)) {
    return <Navigate to={fallbackPath} replace />;
  }

  if (allowedRoles?.length && !allowedRoles.includes(role)) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Skip permission check for the special Leader case
  if (
    !(
      requiredPermission === "viewResidentDashboard" &&
      role === "Leader" &&
      sessionStorage.getItem("viewingResidentId")
    )
  ) {
    // Normal permission check
    if (requiredPermission && !checkPermission(requiredPermission)) {
      console.log("[DEBUG] Permission Denied:", requiredPermission);
      return <Navigate to={fallbackPath} replace />;
    }
  }

  if (additionalCheck && !additionalCheckPassed) {
    return <Navigate to={fallbackPath} replace />;
  }

  return children;
};

export { AuthenticationProvider, defaultPermissions };
